<script setup lang="ts">
import {
    faBell as farBell,
    faBellPlus as farBellPlus,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faBell as fasBell,
    faBellPlus as fasBellPlus,
} from '@fortawesome/pro-solid-svg-icons';
import type TripPackage from '@itinerary/trip/TripPackage';
import { type ClassOfServiceTypes, ServiceType } from '@clients/PricetrackApi';
import { usePriceTrackModal } from '@composable/usePriceTrackModal';
import type { IconProp } from '#imports';
import { faAutoReserveSolid } from '@static/customIcons';
import useAutoReserveModal from '@composable/useAutoReserveModal';
import type TrainPackage from '@itinerary/trip/train/TrainPackage';
import { useCurrentSearchForm } from '@composable/useCurrentSearchForm';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import CircleWaves from '@components/ui/hotelPage&view/CircleWaves.vue';
import { TrackingType } from '@itinerary/types';
import type FlightPackage from '@itinerary/trip/flight/FlightPackage';
import {
    type CabinClassDto,
    FlightInfoApiApiFlightInfoGetCabinClassessGet,
} from '@clients/FlightApi';

const {
    mobileHideTooltip,
    showTooltip,
    tripPackage,
    white,
    inDetails,
    type = 'notify',
} = defineProps<IProps>();

const searchFormStore = useSearchFormStore();
const { openPriceTrackModal } = usePriceTrackModal();
const { reserveType } = storeToRefs(searchFormStore);
const priceTrackStore = usePriceTrackStore();
const { priceTrackList, priceTrackInfo } = classToRefs(priceTrackStore);
const analyticsStore = useAnalyticsStore();
const autoReserveStore = useAutoReserveStore();
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
interface IProps {
    tripPackage: TripPackage;
    inDetails?: boolean;
    white?: boolean;
    showTooltip?: boolean;
    mobileHideTooltip?: boolean;
    type?: 'notify' | 'reserve';
}
const { isMobile } = useSize();
const { value, addToCache } = await useDataCache<CabinClassDto[]>(
    'flight-cabin-classes'
);
const { openAutoReserveModal } = useAutoReserveModal();
const observer = ref<IntersectionObserver>();
const cid = computed(() => {
    if (type === 'reserve') {
        return 'auto-reserve';
    }
    switch (trackingType.value) {
        case TrackingType.Price:
            return 'price-tracking';
        case TrackingType.Capacity:
            return 'capacity-tracking';
        default:
            return undefined;
    }
});
const tooltipDescription = computed(() => {
    if (type === 'reserve') {
        return 'ارزان شد رزرو کن';
    }
    switch (trackingType.value) {
        case TrackingType.Price:
            return 'ارزان شد خبرم کن';
        case TrackingType.Capacity:
            return 'موجود شد خبرم کن';
        default:
            return '';
    }
});
const icon = computed(() => {
    if (type === 'reserve') {
        return faAutoReserveSolid;
    }
    switch (trackingType.value) {
        case TrackingType.Price:
            return tracked.value ? fasBell : farBell;
        case TrackingType.Capacity:
            return tracked.value ? fasBellPlus : farBellPlus;
        default:
            return undefined;
    }
});
const trackingType = computed(() => {
    if (tripPackage?.isFull) {
        return TrackingType.Capacity;
    }
    return TrackingType.Price;
});
const tracked = computed(() => !!priceTrack.value);
const priceTrack = computed(() =>
    priceTrackList.value.find(
        priceTrack =>
            `${priceTrack.serviceNumber}` === tripPackage.serviceNumber &&
            priceTrack.serviceId === tripPackage.serviceId &&
            priceTrack.notify
    )
);
const serviceTypeName = computed(() => {
    switch (reserveType.value) {
        case 'flight':
            return ServiceType.NUMBER_0;
        case 'train':
            return ServiceType.NUMBER_1;
        case 'bus':
            return ServiceType.NUMBER_2;
        default:
            return undefined;
    }
});
const show = computed(() => {
    if (type === 'reserve') {
        return tripPackage.hasAutoReserve && tripPackage.reservable;
    }
    switch (trackingType.value) {
        case TrackingType.Price:
            return (
                tripPackage.haveDecreasingPriceNotifier &&
                tripPackage.canTrackPackageCapacity
            );
        case TrackingType.Capacity:
            return (
                tripPackage.havePriceTrack &&
                tripPackage.canTrackPackageCapacity
            );
        default:
            return tripPackage.canTrackPackageCapacity;
    }
});
const buttonIconProperties = computed<IconProp | undefined>(() => {
    if (!icon.value) {
        return undefined;
    }
    return {
        icon: icon.value,
        color: white ? 'white' : undefined,
    };
});
const startObserving = () => {
    if (observer.value) {
        return;
    }
    observer.value = new IntersectionObserver(
        entries => {
            if (entries[0].isIntersecting) {
                observer.value?.disconnect();
            }
        },
        {
            threshold: [0, 0.2],
            root: null,
            rootMargin: '0px 0px 0px 0px',
        }
    );
    const instanceEl = getCurrentInstance()?.vnode?.el;
    if (instanceEl) {
        observer.value.observe(instanceEl as Element);
    }
};
const trackCard = async () => {
    analyticsStore.trackPosthog(`click-on-track-card-${cid.value}`);
    if (type === 'reserve') {
        autoReserveStore.clearFilters();
        autoReserveStore.preSelectPackage = tripPackage;
        autoReserveStore.selectedPackages = [tripPackage];
        autoReserveStore.showAll = true;
        openAutoReserveModal('auto-reserve-tracking-button-click');
    } else if (tracked.value && priceTrack.value) {
        try {
            await priceTrackStore.setPriceTrackAlert({
                priceTrackId: priceTrack.value.trackId,
                priceHash: priceTrack.value.hashId,
                notify: false,
            });
            priceTrackStore.loadPriceTracks();
        } catch (error) {
            handleException(error);
        }
    } else {
        if (!tripPackage) {
            return;
        }
        let classes = value;
        if (!value) {
            const { data } =
                await FlightInfoApiApiFlightInfoGetCabinClassessGet();
            classes = data;
            addToCache(data);
        }
        priceTrackInfo.value = {
            corporationCode: tripPackage.firstTrip.corporationInfo.code,
            corporationName: tripPackage.firstTrip.corporationInfo.title,
            originCode: tripPackage.originCode,
            destinationCode: tripPackage.destinationCode,
            origin: tripPackage.origin,
            destination: tripPackage.destination,
            englishDestination: currentSearchForm.value.destination?.code,
            englishOrigin: currentSearchForm.value.origin?.code,
            date: tripPackage.departureDateTime.toISOString(),
            arrivalTime: tripPackage.arrivalTime24,
            departureTime: tripPackage.departureTime24,
            arrivalDateTime: tripPackage.arrivalDateTime,
            departureDateTime: tripPackage.departureDateTime,
            logoUrl: tripPackage.firstTrip.corporationInfo.logoUrl,
            serviceId: tripPackage.serviceId,
            serviceNumber: tripPackage.serviceNumber,
            dateString: dayConfig
                .jalali(tripPackage.segments[0].trips[0].departureDateTime)
                .format(
                    formatter({
                        format: 'long',
                        includeWeekday: true,
                    })
                ),
            price: tripPackage.singlePayable,
            serviceType: serviceTypeName.value,
            type: tripPackage.reservable
                ? TrackingType.Price
                : TrackingType.Capacity,
            priceTrackOnPath: false,
            genderCode: (tripPackage as TrainPackage).defaultSellType?.code,
            isInternational: !currentSearchForm.value.isDomestic,
            classOfService: classes?.find(
                ({ ClassOfService }) =>
                    (tripPackage as FlightPackage)?.defaultPrice?.cabinClass ===
                    ClassOfService
            )?.Id as ClassOfServiceTypes,
        };
        await openPriceTrackModal({
            tripPackage,
            price: tripPackage.singlePayable,
            trackingType: trackingType.value,
            trackScope: 'ticket',
        });
    }
};
watch(
    show,
    val => {
        if (val) {
            startObserving();
        }
    },
    { immediate: true }
);
</script>

<template>
    <div @click.stop>
        <div
            v-if="show"
            :id="cid"
            class="tracking-button-container"
            :class="{ selected: tracked, 'in-details': inDetails }"
            @click.stop="trackCard"
        >
            <CircleWaves
                class="waves"
                :from-scale="isMobile ? 'scale(1, 1)' : 'scale(0.8, 0.8)'"
                :to-scale="isMobile ? 'scale(1.2, 1.3)' : 'scale(1.1, 1.1)'"
            />
            <mr-button
                variant="transparent"
                full-width
                :size="{ desktop: 'small', mobile: 'medium' }"
                :icon-button="inDetails ? undefined : buttonIconProperties"
                :right-icon="!inDetails ? undefined : buttonIconProperties"
                class="tracking-button"
                name="price-track"
            >
                <span v-if="inDetails">
                    {{ tooltipDescription }}
                </span>
            </mr-button>
            <div
                class="tooltip-container"
                :class="[
                    { show: showTooltip, 'mobile-hide': mobileHideTooltip },
                    cid,
                ]"
            >
                {{ tooltipDescription }}
                <svg width="5" height="8" viewBox="0 0 5 8" fill="none">
                    <path d="M0 4L5 0V8L0 4Z" fill="var(--cool-gray-500)" />
                </svg>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@include v-transition-opacity;
.tracking-button-container {
    position: relative;
    display: flex;
    align-items: center;
    align-self: center;
    .tracking-button:hover {
        ~ .tooltip-container {
            opacity: 1;
        }
    }
    @include mobile-only() {
        margin: 0;
    }
    &.in-details {
        align-self: center;
        margin: auto;
    }
}

.waves {
    display: none;
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.shepherd-enabled.shepherd-target {
    .waves {
        display: flex;
    }
}

// eslint-disable-next-line vue-scoped-css/require-selector-used-inside,vue-scoped-css/no-unused-selector
.tooltip-container {
    pointer-events: none;
    @include mobile-only() {
        @include tg-s($w5, usePalette());
        &.price-tracking {
            display: none;
        }
        svg {
            display: none;
        }
        &.mobile-hide {
            display: none;
        }
    }
    @include desktop-only {
        @include tg-xs($w7, white);
        position: absolute;
        right: 0;
        top: 25%;
        border-radius: 8px;
        background-color: usePalette($p-cool-gray, 500);
        white-space: nowrap;
        width: 114px;
        height: 28px;
        padding: 4px 8px;
        transform: translateX(100%);
        opacity: 0;
        transition: opacity 200ms linear;
        &.show {
            opacity: 1;
        }
        svg {
            position: absolute;
            left: -4px;
            top: 8px;
        }
    }
}
</style>
